import { BackButton, FilteredCreditApplication } from '@applyonline/shared'
import { addMonths, DateOnly } from '@if/core/date'
import { formatCurrency, formatDate } from '@if/core/utils'
import { EPaymentPlan } from '@if/core/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetPromotionalCreditAppPlan } from '../../../../api/compute-api'
import ManageCoappButton from '../../../../components/ManageCoappButton'
import RemoveCoappButton from '../../../../components/RemoveCoappButton'
import { applicantFullName, getLoanAmount, hasSignedContract } from '../../../../data/credit-application-selectors'
import { useCalculateTermPlanOptions } from '../../../../api/term-plan-api'

interface Props {
  creditApp: FilteredCreditApplication
}

const CreditSummaryPage = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const creditApplicationId = creditApp.id
  const termPlanOptionsRequest = useMemo(
    () => ({
      amountRequested: getLoanAmount(creditApp),
      deliveryOn: formatDate(DateOnly.today()),
      firstPaymentOn: formatDate(addMonths(DateOnly.today(), 1)),
      appliedPaymentPlanId: null,
      selectedPaymentPlanId:
        creditApp.merchantPaymentPlanId &&
        creditApp.merchantPaymentPlanId !== (EPaymentPlan.RegularDailyInterests as string)
          ? creditApp.merchantPaymentPlanId
          : null,
    }),
    [creditApp],
  )
  const [monthlyPaymentPerMonths, isLoadingTerms] = useCalculateTermPlanOptions(creditApp, termPlanOptionsRequest)
  const [promotionalAmounts] = useGetPromotionalCreditAppPlan(
    getLoanAmount(creditApp),
    creditApplicationId,
    creditApp.merchantPaymentPlanId,
  )
  const minTermPayment = useMemo(
    () => (monthlyPaymentPerMonths ? monthlyPaymentPerMonths[monthlyPaymentPerMonths.length - 1] : null),
    [monthlyPaymentPerMonths],
  )
  const maxTermPayment = useMemo(
    () => (monthlyPaymentPerMonths ? monthlyPaymentPerMonths[0] : null),
    [monthlyPaymentPerMonths],
  )
  const hasPromoPlanPayment =
    monthlyPaymentPerMonths?.some(
      (payment) => payment.paymentPlanId != null && payment.paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string),
    ) ?? false
  const hasSignedCvt = hasSignedContract(creditApp)

  const shouldShowHasUniqueFee = (creditApp: FilteredCreditApplication) => {
    return creditApp?.normsDecision.lenderFeeRate > 0
  }

  const buildLoanInterestAndFeeText = (creditApp: FilteredCreditApplication) => {
    return (
      t('common.monthlyAmountsInclude', {
        interestRate: creditApp?.finalDecision.interestRate,
      }) + (shouldShowHasUniqueFee(creditApp) ? t('common.loanHasOneTimeFee') : '.')
    )
  }

  if (isLoadingTerms) return null
  return (
    <main className="form-section">
      <div className="credit-steps">
        <div className="applicationInfo-card paragraph">
          <h3>{t('common.appNumber', { number: creditApp.referenceNumber })}</h3>
          <div>
            <p className="applicationInfo-card-subtitle">
              <strong>{t('common.applicant')} : </strong>
              {applicantFullName(creditApp.applicant)}
            </p>
            <p className="applicationInfo-card-subtitle">
              <strong>{t('common.coapplicant')} : </strong>
              {creditApp.coapplicant !== null ? applicantFullName(creditApp.coapplicant) : t('common.none')}
            </p>
          </div>
          {!hasSignedCvt && (
            <div className="btn-group space-between">
              <ManageCoappButton
                creditAppId={creditApp.id}
                useChangeLabel={creditApp.coapplicant !== null}
                merchantId={creditApp.merchantId}
                isHardDecline={creditApp.finalDecision.isHardDecline}
              />
              {creditApp.coapplicant !== null && creditApp.applicant.hasCreditReport && (
                <RemoveCoappButton creditAppId={creditApp.id} />
              )}
            </div>
          )}
        </div>
        <div className="applicationInfo-card paragraph">
          <h3>{t('loan.details')}</h3>
          <div>
            <p>
              <strong>{t('common.maxLoanAmount')} : </strong>
              {formatCurrency(creditApp.finalDecision.maxAmountFinanced)}
            </p>
            <p>
              <strong>{t('common.requestedAmount')} : </strong>
              {formatCurrency(creditApp.initialRequestedLoanAmount)}
            </p>
            {creditApp.merchantPaymentPlan === null && minTermPayment && maxTermPayment && (
              <>
                <p>
                  <strong>{t('common.monthlyPaymentMin')} : </strong>
                  {minTermPayment
                    ? t('loan.monthPeriodItem', {
                        loanTerm: minTermPayment.term,
                        loanAmount: formatCurrency(minTermPayment.amountWithoutInsurance),
                      })
                    : t('common.n/a', { ns: 'shared' })}
                </p>
                <p>
                  <strong>{t('common.monthlyPaymentMax')} : </strong>
                  {maxTermPayment
                    ? t('loan.monthPeriodItem', {
                        loanTerm: maxTermPayment.term,
                        loanAmount: formatCurrency(maxTermPayment.amountWithoutInsurance),
                      })
                    : t('common.n/a', { ns: 'shared' })}
                </p>
                <p>
                  {hasPromoPlanPayment
                    ? t('loan.estimatedPaymentsPerFinancingOption')
                    : buildLoanInterestAndFeeText(creditApp)}
                </p>
              </>
            )}
            {creditApp.merchantPaymentPlan !== null && promotionalAmounts && (
              <>
                <p>
                  <strong>{t('common.monthlyPayment')} : </strong>
                  {t('loan.monthPeriodItem', {
                    loanTerm: creditApp.merchantPaymentPlan.loanTerm,
                    loanAmount: formatCurrency(promotionalAmounts.paymentForFrequency),
                  })}
                </p>
                {creditApp.merchantPaymentPlan.borrowerFeeRate > 0 && (
                  <p>{t('decision.includedFee', { adminFeeRate: creditApp.merchantPaymentPlan.borrowerFeeRate })}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <BackButton />
    </main>
  )
}

export default CreditSummaryPage
